<template>
  <div id="edit_call_campaign">
    <a-button
      class="secondary_large secondary_large_dark"
      @click="handleSettingsClick"
    >
      Settings

      <img
        class="size-5"
        :src="imagePath.dark.action.settings"
        alt="settings_icon"
      />
    </a-button>

    <CustomModal
      title="Settings"
      v-model:visible="showEditModal"
      :width="'42vw'"
    >
      <template v-if="campaignType === dialerTypes.VOICE_AGENTS_DIALER">
        <CreateOrEditVoiceAgentCampaign
          source="edit_call_campaign"
          :is-edit="true"
          :campaign-data="campaignData"
          @update:formData="handleFormDataUpdate"
          @update:isValid="handleFormValidityUpdate"
        />
      </template>
      <template v-else>
        <CreateOrEditCallCampaign
          source="edit_call_campaign"
          :is-edit="true"
          :campaign-data="campaignData"
          @update:formData="handleFormDataUpdate"
          @update:isValid="handleFormValidityUpdate"
        />
      </template>

      <template #footer>
        <a-button
          class="primary_medium"
          @click="toEditCallCampaign"
          :loading="updating"
          :disabled="!isFormValid"
          >Save Changes</a-button
        >
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { computed, ref, toRaw } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { callCampaignStatuses, dialerTypes } from "@/src/constants/callConstants.js";
import store from "@/src/store/index.js";
import CallService from "@/src/api/CallService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import CreateOrEditCallCampaign from "@/src/views/calls/call_campaigns/CreateOrEditCallCampaign.vue";
import CreateOrEditVoiceAgentCampaign from "@/src/views/voice_agents/CreateOrEditVoiceAgentCampaign.vue";
import imagePath from "@/src/paths/imagePath.js";
import dayjs from "dayjs";

const props = defineProps({
  source: { type: String, required: true },
});

const updating = ref(false);
const showEditModal = ref(false);
const updatedFormData = ref(null);
const isFormValid = ref(true);

const liveCampaignData = computed(
  () => store.getters["callsCampaign/campaignDetails"],
);

const campaignData = ref(null);

const campaignStatus = computed(
  () => store.getters["callsCampaign/campaignStatus"],
);

const campaignType = computed(
  () => store.getters["callsCampaign/campaignType"]
);

const handleFormDataUpdate = (newFormData) => {
  updatedFormData.value = newFormData;
};

const handleFormValidityUpdate = (isValid) => {
  isFormValid.value = isValid;
};

const handleSettingsClick = () => {
  try {
    campaignData.value = structuredClone(toRaw(liveCampaignData.value));
    showEditModal.value = true;
  } catch (error) {
    console.error("An error occurred while handling settings click: ", error);
  }
};

const toEditCallCampaign = async () => {
  try {
    if (!updatedFormData.value) {
      message.warning("No changes detected");
      return;
    }

    updating.value = true;

    const payload = {
      phone_number_id: updatedFormData.value?.phoneNumberId,
      voicemail_id: updatedFormData.value?.voicemailId,
      cooldown_time: updatedFormData.value?.cooldownDuration,
      campaign_type: updatedFormData.value?.dialerType,
      parallel_calls: updatedFormData.value?.parallelCalls,
      entity_list_id: updatedFormData.value?.entityListId,
      dnc_follow: updatedFormData.value?.dncFollow,
    };

    if (updatedFormData.value?.dialerType === dialerTypes.POWER_DIALER) {
      payload.number_selection = updatedFormData.value?.numberSelection;
    }

    if (updatedFormData.value?.useLocalTimeZone) {
      payload.day_allowed = updatedFormData.value?.dayAllowed;
      if (updatedFormData.value?.startTime) {
        payload.start_time = dayjs(updatedFormData.value.startTime, ["h:mma", "h:mm a"]).format("HH:mm");
      }
      if (updatedFormData.value?.endTime) {
        payload.end_time = dayjs(updatedFormData.value.endTime, ["h:mma", "h:mm a"]).format("HH:mm");
      }
    } else {
      payload.day_allowed = null;
      payload.start_time = null;
      payload.end_time = null;
    }

    const response = await CallService.updateCallCampaign(
      campaignData.value?.id,
      payload,
    );
    if (response?.success) {
      await store.dispatch(
        "callsCampaign/updateCampaignDetails",
        response?.campaign,
      );
      message.success(
        campaignStatus.value === callCampaignStatuses.IN_PROGRESS
          ? "Changes will be saved after the ongoing call is ended"
          : "Call campaign updated successfully!!",
      );
    }
  } catch (error) {
    console.error("An error occurred while editing call campaign: ", error);
    message.error("Failed to update call campaign");
  } finally {
    updating.value = false;
    showEditModal.value = false;
  }
};
</script>
